import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { requestNotificationPermission, isNotificationEnabled, isPushServiceAvailable } from '../../services/onesignal';
import { isNative, nativeRequestPushPermission, nativePushPermissionGranted } from '../../capacitor';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';

const DISMISS_KEY = 'pushPromptDismissed';
const SNOOZE_DAYS = 7;

const isNotificationSupported = () => {
  return isNative || (typeof window !== 'undefined' && 'Notification' in window);
};

const isSnoozed = () => {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  return dismissed && Date.now() - parseInt(dismissed, 10) < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
};

const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useApp();
  const location = useLocation();
  const navCount = useRef(0);
  const surfaced = useRef(false);

  const isCheckoutPage = location.pathname.startsWith('/checkout');
  const isChatThread = /^\/messages\/.+/.test(location.pathname);

  useEffect(() => {
    navCount.current += 1;
    if (surfaced.current || !isAuthenticated || !isNotificationSupported() || isSnoozed()) return;
    if (navCount.current < 3) return; // the perfect moment: they're settled in and browsing

    const check = async () => {
      if (isNative) {
        if (await nativePushPermissionGranted()) return;
      } else if (isNotificationEnabled()) {
        return;
      }
      surfaced.current = true;
      setTimeout(() => setShowPrompt(true), 1500);
    };
    check();
  }, [location, isAuthenticated]);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const granted = isNative ? await nativeRequestPushPermission() : await requestNotificationPermission();
      if (granted) {
        toast.success("You're all set — we'll only ping you when it matters");
        localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
        setShowPrompt(false);
      } else if (!isNative && !isPushServiceAvailable()) {
        toast.error('Notification service is temporarily unavailable — you will still receive email alerts.');
        handleDismiss();
      } else {
        toast.error('Please allow notifications in your device settings');
        handleDismiss();
      }
    } catch (error) {
      console.error('Push notification error:', error);
      handleDismiss();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || isCheckoutPage || isChatThread) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-[380px] z-40 animate-in slide-in-from-bottom-4 fade-in duration-500 checkout-safe"
      data-testid="notification-prompt-card"
    >
      <div className="relative rounded-2xl shadow-2xl p-5" style={{ backgroundColor: '#1a3a2a', color: '#ffffff', border: '1px solid rgba(201,169,98,0.35)' }}>
        <button
          onClick={handleDismiss}
          disabled={isLoading}
          className="absolute top-3 right-3 transition-colors"
          style={{ color: 'rgba(255,255,255,0.45)' }}
          data-testid="notification-prompt-dismiss"
          aria-label="Not now"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(201,169,98,0.2)' }}>
            <Bell className="w-5 h-5" style={{ color: '#c9a962' }} />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ fontFamily: 'Georgia, serif', color: '#ffffff' }}>
              Never miss a moment on Minted
            </p>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
              A gentle nudge when someone messages you, makes an offer, or your item sells. No spam — promise.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleEnable}
                disabled={isLoading}
                className="text-xs font-semibold px-4 py-2 rounded-full transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#c9a962', color: '#1a3a2a' }}
                data-testid="notification-prompt-enable"
              >
                {isLoading ? 'One sec…' : 'Turn on notifications'}
              </button>
              <button
                onClick={handleDismiss}
                disabled={isLoading}
                className="text-xs px-3 py-2 transition-colors"
                style={{ color: 'rgba(255,255,255,0.65)' }}
                data-testid="notification-prompt-later"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
